import { Injectable } from '@angular/core';
import { BaseClient } from '../common/client/base.client';
import { SvcClients } from '../common/client/SvcClients';

@Injectable({
  providedIn: 'root',
})
export class SecurityClient extends BaseClient {
  constructor() {
    super();
  }

  login(username: string, password: string, message: string) {
    return this.post(
      SvcClients.AdminSvc,
      'PayDayAdminPortal/authenticate',
      { username: username, password: password },
      message,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      'response'
    );
  }

  refreshToken(refreshToken: string) {
    return this.postRefreshToken(
      SvcClients.AdminSvc,
      'PayDayAdminPortal/refreshtoken',
      refreshToken,
      undefined,
      'response'
    );
  }

  exchangeToken(oneTimeCode: string) {
    return this.post
      (
        SvcClients.AdminSvc,
        'Saml/ExchangeToken',
        {
          Code: oneTimeCode,
        },
        null,
        null,
        null,
        null,
        null,
        null,
        'response'
      );
  }

  menu(message?: string, menuType?: number) {
    return this.get(
      SvcClients.AdminSvc,
      `PayDayAdminPortal/menu` + (menuType ? `?menuType=${menuType}` : ``),
      message
    );
  }

  userPermissions(message?: string) {
    return this.get(
      SvcClients.AdminSvc,
      `PayDayAdminPortal/userPermissions`,
      message
    );
  }

  signUp(signUpModel: any, message: string) {
    return this.post(
      SvcClients.AdminSvc,
      'UserEmployee/UserEmployeeSignUp', //'v2/RegisterAccount',
      signUpModel,
      message
    );
  }

  changePassword(password: string, newPassword: string, message: string) {
    return this.post(
      SvcClients.AdminSvc,
      'Account/changePassword',
      { password: password, newPassword: newPassword },
      message
    );
  }

  acknowledge(agree: boolean, skipErrorNotification: boolean) {
    return this.put(
      SvcClients.AdminSvc,
      `UserAgreement/acknowledge?agree=${agree}`,
      null,
      null,
      null,
      null,
      null,
      skipErrorNotification
    );
  }

  getAccountDetails(message: string, withCustomers: boolean, userCode?) {
    return this.get(
      SvcClients.AdminSvc,
      `G5AccountDetails/GetAccountDetails?${this.userCodeParam(userCode)}withCustomers=${withCustomers}`,
      message
    );
  }

  getAccountDetailsWithActiveCustomers(
    message: string,
    withCustomers: boolean,
    userCode?
  ) {
    return this.get(
      SvcClients.AdminSvc,
      `G5AccountDetails/GetAccountDetailsWithActiveCustomers?${this.userCodeParam(userCode)}withCustomers=${withCustomers}`,
      message
    );
  }

  // userCode is optional (the API defaults to the logged-in user), so leave it out instead of sending "undefined"/"null"
  private userCodeParam(userCode?: number): string {
    return userCode != null ? `userCode=${userCode}&` : '';
  }

  setAccountDetails(message: string, model: any) {
    return this.post(
      SvcClients.AdminSvc,
      'G5AccountDetails/UpdateAccountDetails',
      model,
      message
    );
  }

  captchaVerify(message: string, captchaToken: any) {
    return this.get(
      SvcClients.AdminSvc,
      `Account/CaptchaVerify?captchaToken=${captchaToken}`,
      message
    );
  }

  sendForgotPasswordInquiry(
    message: string,
    captchaToken: string,
    emailOrPhone: string,
    username: string
  ) {
    const trimmedUsername = typeof username === 'string' ? username.trim() : username;
    return this.post(
      SvcClients.AdminSvc,
      'PDAPAccount/ForgotPassword?captchaToken',
      { PhoneOrEmail: emailOrPhone, UserName: trimmedUsername, CaptchaToken: captchaToken },
      message
    );
  }

  resetPassword(message: string, verificationCode: string, password: string) {
    return this.post(
      SvcClients.AdminSvc,
      'Account/ResetPassword',
      { ResetCode: verificationCode, Password: password },
      message
    );
  }

  transferAccountOwnership(message: string, model: any) {
    return this.post(
      SvcClients.AdminSvc,
      `G5AccountDetails/TransferOwnership`,
      model,
      message
    );
  }

  getMobileButtons(message: string, model: any) {
    return this.post(
      SvcClients.AdminSvc,
      `Customer/MobileButtons`,
      model,
      message
    );
  }

  showHelp(key, customerCode) {
    if (!customerCode)
      return this.get(SvcClients.AdminSvc, `PDPHelp/GetHelpPage/?key=${key}`);
    else
      return this.get(
        SvcClients.AdminSvc,
        `PDPHelp/GetHelpPage/?key=${key}&customerCode=${customerCode}`
      );
  }

  sendChatGPT(model) {
    return this.post(
      SvcClients.ResourceSvc,
      `GratChat/ChatCompletetion`,
      model,
      null
    );
  }

  askChatBot(model) {
    return this.post(
      SvcClients.ResourceSvc,
      `ChatBot/AskChatBot`,
      model,
      null
    );
  }

  /**
   * Confirm / Cancel on a scheduling assistant card (US-8.113). The change itself is stored
   * server-side with the chat — only its id travels. Errors are not toasted: 409 (already
   * handled / expired) carries the card's current state, which the widget renders in place.
   */
  confirmScheduleAction(model: { ChatId: string; ActionId: string; CustomerCode: number }) {
    return this.post(SvcClients.ResourceSvc, `ChatBot/ConfirmScheduleAction`, model, null, null, true, null, true);
  }

  /** Confirm all cards of a reply, in order, in one call. The answer is the list of card states. */
  confirmAllScheduleActions(model: { ChatId: string; ActionIds: string[]; CustomerCode: number }) {
    return this.post(SvcClients.ResourceSvc, `ChatBot/ConfirmAllScheduleActions`, model, null, null, true, null, true);
  }

  cancelScheduleAction(model: { ChatId: string; ActionId: string; CustomerCode: number }) {
    return this.post(SvcClients.ResourceSvc, `ChatBot/CancelScheduleAction`, model, null, null, true, null, true);
  }

  /**
   * Opens a generated policy PDF in a new tab. The agent returns the document as an
   * s3:// URI which the browser can't open directly; this fetches the bytes from WA
   * (api/AiConfig/GetPolicy, which streams the PDF inline) and opens the blob. `key`
   * is the S3 object key (the s3://bucket/ prefix already stripped off).
   */
  viewPolicy(key: string) {
    // adminSvcUrl already ends in /api/, so the route omits the api/ prefix.
    return this.openFile(
      SvcClients.AdminSvc,
      'AiConfig/GetPolicy?key=' + encodeURIComponent(key)
    );
  }

  getIdentityProvider(model) {
    return this.post(
      SvcClients.AdminSvc,
      `Saml/GetIdentityProvider`,
      model,
      null
    );
  }

  getActiveServiceStatusMessage(userCode: any, type: number) {
    return this.get(
      SvcClients.ResourceSvc,
      `ServiceStatusMessage/GetActive_V2?userCode=${userCode}&type=${type}`
    );
  }
}
